import { ReactElement, useMemo, useState, useCallback } from 'react';
import {
  DialogContent,
  Stack,
  Typography,
  Avatar,
  createFilterOptions,
  Link,
  Button,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { LoadingButton } from '@mui/lab';
import QRCode from 'react-qr-code';

import {
  CustomAutocomplete,
  UserInfo,
  IsAvailableChip,
} from '~components/index';
import { hasError } from '~utils/index';
import { getDefaultAvatar } from '~utils/user';
import { APP_STORES_INFO } from '~constants/appStoresInfo';

import { useDialogAssignDriverContext } from '../../DialogAssignDriverContext';
import { FIELDS_NAME, initialValues, getDriverNotAvailable } from '../../utils';
import { DialogAssignDriverStoreImg } from '../../styles';

const DialogAssignDriverNormal = (): ReactElement => {
  const {
    refDriverAutocomplete,
    availablesDrivers: { data: availablesDriversOptions },
    selectedDriverAvailable: { selectedLastDriver, selectedUserLoggedDriver },
    setDefaultValueDriverForm,
    loadingSubmit,
    disabledSubmit,
    lastDriverId,
    tabs: { setActiveTab },
    handleOpenDialogInviteDriver,
    excludeIsAvailableOptions,
  } = useDialogAssignDriverContext();

  const {
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    submitForm,
    values,
  } = useFormikContext<typeof initialValues>();

  const [hasChangeDriverInput, setHasChangeDriverInput] = useState(false);

  const lastDriverHelperText = useMemo(() => {
    const selectedDriver = values[FIELDS_NAME.CURRENT_DRIVER];

    if (!selectedDriver) return undefined;

    const isLastDriverSelected = lastDriverId === selectedDriver.id;

    if (!isLastDriverSelected || hasChangeDriverInput) return undefined;

    return 'Se sugiere el último chofer asignado al vehículo de la ruta.';
  }, [lastDriverId, values, hasChangeDriverInput]);

  const handleChangeToFirstRouteTab = useCallback(() => {
    setDefaultValueDriverForm(selectedUserLoggedDriver);
    setActiveTab('firstRoute');
  }, [selectedUserLoggedDriver, setActiveTab, setDefaultValueDriverForm]);

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={1}>
          <Stack textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Selecciona un chofer de la lista para enviarle la ruta
            </Typography>
          </Stack>

          <CustomAutocomplete
            innerRef={refDriverAutocomplete}
            options={availablesDriversOptions}
            label="Chofer"
            name={FIELDS_NAME.CURRENT_DRIVER}
            defaultValue={selectedLastDriver}
            onChange={(_e, newDriver) => {
              setHasChangeDriverInput(true);
              setFieldValue(FIELDS_NAME.CURRENT_DRIVER, newDriver);
            }}
            onBlur={() => {
              setFieldTouched(FIELDS_NAME.CURRENT_DRIVER, true);
            }}
            error={hasError(touched, errors, FIELDS_NAME.CURRENT_DRIVER)}
            helperText={lastDriverHelperText}
            getOptionLabel={(option) => option.displayName}
            getOptionDisabled={(option) =>
              getDriverNotAvailable(option, excludeIsAvailableOptions)
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option.id}
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <UserInfo
                  name={option.displayName}
                  urlAvatar={getDefaultAvatar(option.avatar ?? undefined)}
                />

                {!excludeIsAvailableOptions && (
                  <IsAvailableChip
                    isAvailable={Boolean(option.isAvailable)}
                    size="small"
                    variant="filled"
                  />
                )}
              </li>
            )}
            startAdornment={(val) =>
              val && (
                <Avatar
                  src={getDefaultAvatar(val.avatar ?? undefined)}
                  alt={val?.displayName}
                  sx={{ width: 20, height: 20 }}
                />
              )
            }
            filterOptions={createFilterOptions({
              ignoreAccents: true,
              ignoreCase: true,
              stringify: (option) => option.displayName.replace(',', ''),
            })}
          />

          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Link
              href="https://routix.tawk.help/article/asignar-ruta-a-un-chofer"
              target="_blank"
              underline="hover"
              fontWeight="bold"
              variant="caption"
            >
              Necesito ayuda sobre esto
            </Link>

            <LoadingButton
              variant="contained"
              color="primary"
              onClick={submitForm}
              loading={loadingSubmit}
              disabled={disabledSubmit}
            >
              Asignar
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogContent dividers>
        <Stack spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            También puedes
          </Typography>

          <Stack spacing={2} direction="row">
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleChangeToFirstRouteTab}
            >
              Simular ser el chofer
            </Button>

            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleOpenDialogInviteDriver}
            >
              Invitar a tus choferes
            </Button>
          </Stack>

          <Typography variant="caption" flex={1}>
            <b>Descarga</b> la app e <b>inicia sesión</b> con tu usuario y
            contraseña.
          </Typography>

          <QRCode
            value="https://app.routix.io/public/device_redirect"
            size={120}
          />

          <Stack spacing={1} direction="row" justifyContent="center">
            <Link
              href={APP_STORES_INFO.PLAY_STORE.URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <DialogAssignDriverStoreImg
                src={APP_STORES_INFO.PLAY_STORE.IMAGE_URL}
                alt={APP_STORES_INFO.PLAY_STORE.NAME}
              />
            </Link>

            <Link
              href={APP_STORES_INFO.APP_STORE.URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <DialogAssignDriverStoreImg
                src={APP_STORES_INFO.APP_STORE.IMAGE_URL}
                alt={APP_STORES_INFO.APP_STORE.NAME}
              />
            </Link>
          </Stack>
        </Stack>
      </DialogContent>
    </>
  );
};

export default DialogAssignDriverNormal;
